let _ = t => t,
  _t;
import { Fragment, useCallback, useEffect, useState } from 'react';
import Raven from 'raven-js';
import { createGlobalStyle } from 'styled-components';
import enviro from 'enviro';
import { GRID_BREAKPOINT_MEDIUM } from 'HubStyleTokens/sizes';
import merge from 'hs-lodash/merge';
import getLang from 'I18n/utils/getLang';
import { useWindowDimensionsContext } from '../hooks/useWindowDimensionsContext';
import { MAIN_SECTION_MAX_WIDTH, MAIN_SECTION_PADDING_X } from '../layout/constants';
import { getFullPublicUrl, getPublicLocalizedPathname } from '../utils/urls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const GRID_BREAKPOINT_MEDIUM_INT = parseInt(GRID_BREAKPOINT_MEDIUM, 10);
export const MARKETING_NAV_CDN_SCRIPT_ID = 'marketing-nav-script';
export const MARKETING_NAV_CDN_QA_URL = 'https://www.hubspot.com/assets/nav/next/index.js';
export const MARKETING_NAV_CDN_PROD_URL = 'https://www.hubspot.com/assets/nav/latest/index.js';
const MARKETING_NAV_HEADER_ID = 'marketing-nav-header';
const MARKETING_NAV_FOOTER_ID = 'marketing-nav-footer';
export const MARKETING_NAV_LANGUAGES = {
  en: 'English',
  de: 'Deutsch',
  es: 'Español',
  fr: 'Français',
  it: 'Italiano',
  ja: '日本語',
  nl: 'Nederlands',
  pt: 'Português',
  zh: '中文 - 繁體',
  fi: 'Suomi',
  da: 'Dansk',
  no: 'Norsk',
  pl: 'Polski',
  sv: 'Svenska'
};
export function getMarketingNavLanguagesContent(localizedPathnameMap) {
  const languagesContent = Object.fromEntries(Object.entries(localizedPathnameMap).map(([languageCode, localizedPathname]) => [languageCode, [MARKETING_NAV_LANGUAGES[languageCode], getFullPublicUrl(), localizedPathname]]));
  // marketing nav header uses jp for Japanese
  if ('ja' in languagesContent) {
    languagesContent['jp'] = languagesContent['ja'].slice(0);
    delete languagesContent['ja'];
  }
  return languagesContent;
}
const MarketingNavGlobalStyle = createGlobalStyle(_t || (_t = _`
  .hsg-page-width-normal,
  .hs-page-width-normal {
    max-width: ${0}px !important;
    padding: 0 !important;

    @media (min-width: 768px) {
      padding: 0 ${0}px !important;
    }
  }

  .hsg-nav__group-item.hsg-nav__group-item--cta {
    @media (min-width: 768px) {
      padding-right: 0;
    }
  }

  .hsg-nav__group-item.hsg-nav__group-item--search {
    display: none;
  }

  .hsg-nav__group-item > #hsg-nav__logo > #hsg-nav__logo-mobile {
    display: none;
  }

  .hsg-footer__layout {
    max-width: ${0}px !important;
    padding: 0 !important;

    @media (min-width: 768px) {
      padding: 0 ${0}px !important;
    }
  }
`), MAIN_SECTION_MAX_WIDTH, MAIN_SECTION_PADDING_X, MAIN_SECTION_MAX_WIDTH, MAIN_SECTION_PADDING_X);
export default function MarketingNav({
  _useMarketingNavCdnQa = false,
  children,
  enableConvertFlag = true,
  headerContentBlob = {}
}) {
  const [useMarketingNavCdnQa] = useState(enviro.isQa() && (_useMarketingNavCdnQa || /_marketingNavCdnQa=true/i.test(window.location.search)));
  const [marketingNavReady, setMarketingNavReady] = useState(false);
  const [marketingNavCdnRequestState, setMarketingNavCdnRequestState] = useState('PENDING');
  const marketingNavCdnLoaded = marketingNavCdnRequestState === 'SUCCEEDED' && window.marketingNav != null;
  useEffect(() => {
    const handleMarketingNavCdnLoad = () => {
      setMarketingNavCdnRequestState('SUCCEEDED');
    };
    const handleMarketingNavCdnFailed = () => {
      setMarketingNavCdnRequestState('FAILED');
      setMarketingNavReady(true);
    };
    let marketingNavCdnScript = document.getElementById(MARKETING_NAV_CDN_SCRIPT_ID);
    if (!marketingNavCdnScript) {
      marketingNavCdnScript = document.createElement('script');
      marketingNavCdnScript.setAttribute('src', useMarketingNavCdnQa ? MARKETING_NAV_CDN_QA_URL : MARKETING_NAV_CDN_PROD_URL);
      marketingNavCdnScript.setAttribute('id', MARKETING_NAV_CDN_SCRIPT_ID);
      document.body.appendChild(marketingNavCdnScript);
    }
    marketingNavCdnScript.addEventListener('load', handleMarketingNavCdnLoad);
    marketingNavCdnScript.addEventListener('error', handleMarketingNavCdnFailed);
    return () => {
      if (marketingNavCdnScript) {
        marketingNavCdnScript.removeEventListener('load', handleMarketingNavCdnLoad);
        marketingNavCdnScript.removeEventListener('error', handleMarketingNavCdnFailed);
      }
    };
  }, [useMarketingNavCdnQa]);
  useEffect(() => {
    const marketingNavCdnScript = document.getElementById(MARKETING_NAV_CDN_SCRIPT_ID);
    return () => {
      if (marketingNavCdnScript) {
        marketingNavCdnScript.remove();
      }
    };
  }, []);
  const lang = getLang();
  const getHeaderContentBlob = useCallback(() => {
    const defaultHomeUrl = `${getFullPublicUrl()}${getPublicLocalizedPathname(lang)}`;
    let defaultHeaderContentBlob = {};
    if (marketingNavCdnLoaded) {
      try {
        defaultHeaderContentBlob = window.marketingNav.getNavContentBlob({
          type: 'header',
          locale: lang
        });
      } catch (error) {
        Raven.captureException(error);
        defaultHeaderContentBlob = {};
      }
    }
    return merge({}, defaultHeaderContentBlob, headerContentBlob, {
      upperNavContent: {
        homeUrl: defaultHomeUrl
      },
      mobileNavContent: {
        homeUrl: defaultHomeUrl
      }
    });
  }, [headerContentBlob, lang, marketingNavCdnLoaded]);
  const {
    width: windowWidth
  } = useWindowDimensionsContext();
  const computedHeaderVariation = windowWidth < GRID_BREAKPOINT_MEDIUM_INT ? 'default' : 'marketplace';
  useEffect(() => {
    if (marketingNavCdnLoaded) {
      try {
        window.marketingNav.renderNav({
          type: 'header',
          locale: lang,
          targetId: MARKETING_NAV_HEADER_ID,
          isLoggedIn: false,
          enableConvertFlag,
          variation: computedHeaderVariation,
          contentBlob: getHeaderContentBlob()
        });
        window.marketingNav.renderNav({
          type: 'footer',
          locale: lang,
          targetId: MARKETING_NAV_FOOTER_ID
        });
      } catch (error) {
        Raven.captureException(error);
      }
      setMarketingNavReady(true);
    }
  }, [computedHeaderVariation, enableConvertFlag, getHeaderContentBlob, lang, marketingNavCdnLoaded]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(MarketingNavGlobalStyle, {}), /*#__PURE__*/_jsx("header", {
      id: MARKETING_NAV_HEADER_ID
    }), marketingNavReady && children, /*#__PURE__*/_jsx("footer", {
      id: MARKETING_NAV_FOOTER_ID
    })]
  });
}